import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ListPlus, Link2 } from "lucide-react";
import { mailboxApi, usersApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/SimpleToast";

const PRIOS = ["Niedrig", "Mittel", "Hoch"];
const PRIO_STYLE = { Niedrig: "bg-emerald-50 border-emerald-300 text-emerald-700", Mittel: "bg-amber-50 border-amber-300 text-amber-800", Hoch: "bg-red-50 border-red-300 text-red-700" };
const intro = (e) => `Aus E-Mail „${e.subject}“ von ${e.from_name || e.from_email}${e.excerpt ? `:\n${e.excerpt}` : ""}`;

export function TaskFromEmailDialog({ email: e, topicName, open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ title: e.subject || "", description: intro(e), assigned_to_user_id: "", due_date: "", priority: "Mittel", notify_email: true });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  useEffect(() => { usersApi.getAll().then((r) => setUsers((r.data || []).filter((u) => u.is_active !== false))).catch(() => {}); }, []);

  const submit = async () => {
    if (!f.title.trim() || !f.assigned_to_user_id) { toast.error("Bitte Titel und Kollegen wählen"); return; }
    setBusy(true);
    try {
      await mailboxApi.createTask(e.id, { ...f, due_date: f.due_date || null });
      toast.success("Aufgabe erstellt – Mail ist jetzt „in Bearbeitung“");
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aufgabe konnte nicht erstellt werden");
    } finally { setBusy(false); }
  };

  const ctx = [e.status === "assigned" && e.target_name, topicName].filter(Boolean);
  const ini = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="mail-task-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ListPlus className="w-5 h-5 text-[#1E3A5F]" />Aufgabe aus E-Mail</DialogTitle>
          <DialogDescription className="sr-only">Aufgabe für einen Kollegen anlegen</DialogDescription>
        </DialogHeader>
        <div className={`rounded-lg border px-3 py-2 text-xs flex flex-wrap items-center gap-x-2 gap-y-1 ${ctx.length ? "bg-[#F0F4F9] border-[#1E3A5F]/10 text-[#1E3A5F]" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="mail-task-context">
          <Link2 className="w-3.5 h-3.5" />
          {ctx.length ? <>Verknüpft mit {ctx.map((c) => <span key={c} className="font-semibold">{c}</span>).reduce((a, b) => [a, " · ", b])}</> : "Diese Mail ist noch keiner Praxis zugeordnet."}
        </div>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Titel</Label><Input value={f.title} onChange={set("title")} data-testid="mail-task-title" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Für Kollegen</Label>
              <Select value={f.assigned_to_user_id} onValueChange={set("assigned_to_user_id")}>
                <SelectTrigger data-testid="mail-task-assignee"><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
                <SelectContent>{users.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`mail-task-assignee-${u.id}`}>
                    <span className="inline-flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-[#1E3A5F] text-white text-[10px] font-semibold flex items-center justify-center">{ini(u.full_name)}</span>{u.full_name}{u.id === user?.id ? " (ich)" : ""}</span>
                  </SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Fällig am</Label><Input type="date" value={f.due_date} onChange={set("due_date")} data-testid="mail-task-due" /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Priorität</Label>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" data-testid="mail-task-priority">
              {PRIOS.map((p) => (
                <button key={p} type="button" role="radio" aria-checked={f.priority === p} onClick={() => set("priority")(p)}
                  className={`h-9 rounded-lg border text-sm font-medium transition-colors ${f.priority === p ? PRIO_STYLE[p] : "bg-background text-muted-foreground hover:bg-muted"}`}
                  data-testid={`mail-task-priority-${p.toLowerCase()}`}>{p}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5"><Label>Beschreibung</Label><Textarea rows={4} value={f.description} onChange={set("description")} data-testid="mail-task-description" /></div>
          <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm cursor-pointer">
            <span><span className="font-medium">Per E-Mail informieren</span><span className="block text-xs text-muted-foreground">Zusätzlich zum Hinweis im CRM</span></span>
            <Switch checked={f.notify_email} onCheckedChange={set("notify_email")} data-testid="mail-task-notify-email" />
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="mail-task-cancel">Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#1E3A5F] hover:bg-[#142E4A] text-white" data-testid="mail-task-submit">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ListPlus className="w-4 h-4 mr-2" />}Aufgabe erstellen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
